import { injectable } from 'inversify';
import type {
  IAdicionalRepository,
  AdicionalFormData,
} from '@/domain/repositories/IAdicionalRepository';
import { Adicional, type AdicionalTipo } from '@/domain/entities/Adicional';
import { httpClient } from '@/infrastructure/http/client/httpClient';

@injectable()
export class ApiAdicionalRepository implements IAdicionalRepository {
  async getAll(search?: string, activo?: boolean): Promise<Adicional[]> {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (activo !== undefined) params.set('activo', String(activo));
      const query = params.toString();
      const response = await httpClient.get<any[]>(
        `/admin/adicionales${query ? `?${query}` : ''}`
      );
      return response.data.map((item) => this.mapToAdicional(item));
    } catch (error) {
      console.error('Error fetching adicionales:', error);
      throw error;
    }
  }

  async getRecurrentes(): Promise<Adicional[]> {
    try {
      const response = await httpClient.get<any[]>('/admin/adicionales/recurrentes');
      const data = response?.data;
      if (!Array.isArray(data)) return [];
      const result: Adicional[] = [];
      for (const item of data) {
        if (item == null || typeof item !== 'object') continue;
        try {
          result.push(this.mapToAdicional(item));
        } catch (e) {
          console.warn('Item inválido en adicionales recurrentes:', item, e);
        }
      }
      return result;
    } catch (error) {
      console.error('Error fetching adicionales recurrentes:', error);
      return [];
    }
  }

  async getById(id: number): Promise<Adicional> {
    try {
      const response = await httpClient.get<any>(`/admin/adicionales/${id}`);
      return this.mapToAdicional(response.data);
    } catch (error) {
      console.error('Error fetching adicional:', error);
      throw error;
    }
  }

  async create(data: AdicionalFormData): Promise<Adicional> {
    try {
      const response = await httpClient.post<any>('/admin/adicionales', {
        nombre: data.nombre,
        descripcion: data.descripcion || undefined,
        precioUnitario: data.precioUnitario,
        unidad: data.unidad || undefined,
        tipo: data.tipo,
        cupoMensajes: data.cupoMensajes,
        esRecurrente: data.esRecurrente,
        activo: data.activo,
      });
      return this.mapToAdicional(response.data);
    } catch (error) {
      console.error('Error creating adicional:', error);
      throw error;
    }
  }

  async update(id: number, data: AdicionalFormData): Promise<Adicional> {
    try {
      const response = await httpClient.put<any>(`/admin/adicionales/${id}`, {
        nombre: data.nombre,
        descripcion: data.descripcion || undefined,
        precioUnitario: data.precioUnitario,
        unidad: data.unidad || undefined,
        tipo: data.tipo,
        cupoMensajes: data.cupoMensajes,
        esRecurrente: data.esRecurrente,
        activo: data.activo,
      });
      return this.mapToAdicional(response.data);
    } catch (error) {
      console.error('Error updating adicional:', error);
      throw error;
    }
  }

  async toggleStatus(id: number): Promise<void> {
    try {
      await httpClient.patch(`/admin/adicionales/${id}/estado`);
    } catch (error) {
      console.error('Error toggling adicional status:', error);
      throw error;
    }
  }

  async delete(id: number): Promise<void> {
    try {
      await httpClient.delete(`/admin/adicionales/${id}`);
    } catch (error) {
      console.error('Error deleting adicional:', error);
      throw error;
    }
  }

  private mapToAdicional(item: any): Adicional {
    const tipo = (item.tipo || 'GENERAL') as AdicionalTipo;
    return Adicional.create(
      item.id,
      item.nombre,
      item.descripcion || '',
      item.precioUnitario ?? 0,
      item.unidad || '',
      item.esRecurrente ?? true,
      item.activo ?? true,
      item.fechaAlta ? new Date(item.fechaAlta) : undefined,
      tipo,
      item.cupoMensajes != null ? Number(item.cupoMensajes) : null
    );
  }
}
